import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import {
  Check, ArrowRight, Database, Activity, Star, Stethoscope,
  Heart, Brain, Dna, FlaskConical, BarChart3, Zap, BookOpen,
  Users, FileText, Shield, Target, Apple, ScanBarcode,
} from "lucide-react";

const PLANS = {
  free: {
    bg: {
      title: "Безплатен план",
      price: "0",
      tagline: "Първи стъпки към по-информирано хранене",
      forWhom: "За всеки, който иска да разбере как храната влияе на тялото му — без ангажимент и без плащане.",
      description: "Безплатният план ви дава достъп до основните инструменти на Nutramilo. Водете хранителен дневник, използвайте здравните калкулатори и разглеждайте научната библиотека. Това е идеалната отправна точка, за да решите дали платформата е подходяща за вас.",
      sections: [
        {
          title: "Какво е включено",
          icon: Check,
          items: [
            { icon: Apple, text: "Хранителен дневник — записвайте какво ядете до 3 дни назад. Виждайте калории, протеин, въглехидрати и мазнини." },
            { icon: Brain, text: "1 AI въпрос на ден — попитайте нашите AI помощници за храна, хранене или здраве." },
            { icon: FlaskConical, text: "11 клинични калкулатора — BMI, eGFR, HOMA-IR, сърдечно-съдов риск, диабет, витамин D, прием на вода, менструален цикъл, фибри, сън и риск от рак на гърдата ()." },
            { icon: BookOpen, text: "Научна библиотека — пълен достъп до рецензирани публикации с директни линкове." },
            { icon: BarChart3, text: "Проследяване на макроси — калории, протеин, въглехидрати, мазнини в реално време." },
          ]
        },
        {
          title: "Какво НЕ е включено",
          icon: Shield,
          excluded: true,
          items: [
            { text: "AI чат и разширени анализи" },
            { text: "Хранителен планировчик (Meal Planner)" },
            { text: "Нутригеномика (генетичен анализ)" },
            { text: "Клиничен анализ" },
            { text: "PDF доклади" },
          ]
        },
        {
          title: "Какво може да постигнете",
          icon: Target,
          items: [
            { text: "Да разберете колко калории приемате и дали храненето ви е балансирано." },
            { text: "Да научите рисковите си фактори чрез безплатните калкулатори." },
            { text: "Да се запознаете с научната основа зад инсулиновите индекси." },
          ]
        }
      ],
      cta: "Регистрирайте се безплатно",
      ctaLink: "/register",
    },
    en: {
      title: "Free Plan",
      price: "0",
      tagline: "First steps toward informed nutrition",
      forWhom: "For anyone who wants to understand how food affects their body — no commitment, no payment.",
      description: "The free plan gives you access to Nutramilo's core tools. Keep a meal diary, use health calculators, and browse the science library.",
      sections: [
        {
          title: "What's included", icon: Check,
          items: [
            { icon: Apple, text: "Meal diary — log what you eat up to 3 days back. See calories, protein, carbs, and fat." },
            { icon: Brain, text: "1 AI question per day — ask our AI assistants about food, nutrition, or health." },
            { icon: FlaskConical, text: "8 health calculators — BMI, eGFR, HOMA-IR, cardiovascular risk, diabetes, vitamin D, water intake, menstrual cycle." },
            { icon: BookOpen, text: "Science library — full access to peer-reviewed publications with direct links." },
            { icon: BarChart3, text: "Macro tracking — calories, protein, carbs, fat in real time." },
          ]
        },
        { title: "What's NOT included", icon: Shield, excluded: true, items: [{ text: "AI chat & advanced analyses" }, { text: "Meal Planner" }, { text: "Nutrigenomics" }, { text: "Clinical Analysis" }, { text: "PDF reports" }] },
        { title: "What you can achieve", icon: Target, items: [{ text: "Understand your caloric intake and nutritional balance." }, { text: "Learn your risk factors through free calculators." }, { text: "Explore the science behind insulin indices." }] }
      ],
      cta: "Register for free", ctaLink: "/register",
    },
    es: {
      title: "Plan Gratuito",
      price: "0",
      tagline: "Primeros pasos hacia una nutrición informada",
      forWhom: "Para cualquiera que quiera entender cómo los alimentos afectan a su cuerpo — sin compromiso, sin pago.",
      description: "El plan gratuito te da acceso a las herramientas principales de Nutramilo. Lleva un diario de comidas, usa calculadoras de salud y explora la biblioteca científica.",
      sections: [
        {
          title: "Qué está incluido", icon: Check,
          items: [
            { icon: Apple, text: "Diario de comidas — registra lo que comes hasta 3 días atrás. Ve calorías, proteínas, carbohidratos y grasas." },
            { icon: Brain, text: "1 pregunta de AI al día — consulta a nuestros asistentes de AI sobre alimentación, nutrición o salud." },
            { icon: FlaskConical, text: "8 calculadoras de salud — BMI, eGFR, HOMA-IR, riesgo cardiovascular, diabetes, vitamina D, ingesta de agua, ciclo menstrual." },
            { icon: BookOpen, text: "Biblioteca científica — acceso completo a publicaciones revisadas por pares con enlaces directos." },
            { icon: BarChart3, text: "Seguimiento de macros — calorías, proteínas, carbohidratos y grasas en tiempo real." },
          ]
        },
        { title: "Qué NO está incluido", icon: Shield, excluded: true, items: [{ text: "Chat de AI y análisis avanzados" }, { text: "Planificador de comidas" }, { text: "Nutrigenómica" }, { text: "Análisis clínico" }, { text: "Informes en PDF" }] },
        { title: "Qué puedes lograr", icon: Target, items: [{ text: "Entender tu ingesta calórica y equilibrio nutricional." }, { text: "Conocer tus factores de riesgo a través de calculadoras gratuitas." }, { text: "Explorar la ciencia detrás de los índices de insulina." }] }
      ],
      cta: "Regístrate gratis", ctaLink: "/register",
    },
    color: "#64748B", icon: Database,
  },
  core: {
    bg: {
      title: "Core план",
      price: "29",
      tagline: "Пълен метаболитен анализ за вашето ежедневие",
      forWhom: "За хора, които искат да управляват храненето си с помощта на AI — да знаят какво да ядат и защо, а не просто да броят калории.",
      description: "Core планът отваря достъп до AI помощниците, хранителния планировчик и метаболитния панел. Получавате персонализирани препоръки на базата на вашия профил, генетични данни и хранителен дневник.",
      sections: [
        {
          title: "Какво е включено",
          icon: Check,
          items: [
            { icon: Brain, text: "AI чат: 100 въпроса/месец — четири специалиста (EndoBot, NutriBot, NephroBot, HemoBot) отговарят на вашите въпроси за хранене, метаболизъм и здраве." },
            { icon: Zap, text: "DIL анализ и метаболитен панел — вашият персонален инсулинов бюджет, метаболитен фенотип (P1-P5) и ежедневно проследяване." },
            { icon: Apple, text: "Хранителен планировчик — 7-дневни AI планове с храни от три бази данни. Всяка храна с оценка за качество и наситеност." },
            { icon: Dna, text: "Нутригеномика (опростен изглед) — качете ДНК файл от 23andMe/Ancestry и вижте кои витамини и минерали са важни за вас." },
            { icon: FlaskConical, text: "Клиничен анализ (опростен изглед) — 10 метаболитни модела анализирани от вашите данни, с ясни препоръки без медицинска терминология." },
            { icon: BarChart3, text: "Седмичен AI отчет — обзор на вашата седмица с оценка, постижения и конкретни съвети за следващата." },
            { icon: ScanBarcode, text: "Четене на баркод и рецепти — прочетете продукта и разгледайте рецепти с пълна хранителна информация." },
            { icon: Zap, text: "40 AI анализа/месец — за разширени функции." },
          ]
        },
        {
          title: "За кого е подходящ",
          icon: Users,
          items: [
            { text: "Хора с метаболитен синдром, инсулинова резистентност или предиабет, които искат да подобрят храненето си." },
            { text: "Жени с хормонален дисбаланс (PCOS, менопауза), които търсят персонализирани хранителни препоръки." },
            { text: "Всеки, който иска да се храни по-осъзнато и да разбере как тялото му реагира на различни храни." },
          ]
        },
        {
          title: "Какво може да постигнете",
          icon: Target,
          items: [
            { text: "По-стабилна кръвна захар и по-малко скокове на инсулина чрез подходящо хранене." },
            { text: "Седмичен преглед на прогреса с конкретни стъпки за подобрение." },
            { text: "Разбиране на генетичните си предразположения към хранителни дефицити." },
          ]
        }
      ],
      cta: "Започнете с Core",
      ctaLink: "/register",
    },
    en: {
      title: "Core Plan", price: "29",
      tagline: "Full metabolic analysis for your daily life",
      forWhom: "For people who want to manage their nutrition with AI — know what to eat and why, not just count calories.",
      description: "Core unlocks AI assistants, the meal planner, and the metabolic panel. Get personalized recommendations based on your profile, genetics, and meal diary.",
      sections: [
        { title: "What's included", icon: Check, items: [
          { icon: Brain, text: "AI chat: 100 Q/month — four specialists answer your nutrition and metabolism questions." },
          { icon: Zap, text: "DIL analysis & metabolic panel — your personal insulin budget and metabolic phenotype." },
          { icon: Apple, text: "Meal planner — 7-day AI plans from three food databases." },
          { icon: Dna, text: "Nutrigenomics (simplified) — upload DNA file, see which vitamins and minerals matter for you." },
          { icon: FlaskConical, text: "Clinical Analysis (simplified) — 10 metabolic patterns with clear recommendations." },
          { icon: BarChart3, text: "Weekly AI report — progress overview with scores and actionable tips." },
          { icon: Zap, text: "40 AI analyses/month for advanced features." },
        ]},
        { title: "Who is it for", icon: Users, items: [
          { text: "People with metabolic syndrome, insulin resistance, or prediabetes." },
          { text: "Women with hormonal imbalance (PCOS, menopause)." },
          { text: "Anyone who wants to eat more mindfully." },
        ]},
        { title: "What you can achieve", icon: Target, items: [
          { text: "More stable blood sugar through informed food choices." },
          { text: "Weekly progress tracking with specific improvement steps." },
          { text: "Understanding your genetic nutrient predispositions." },
        ]}
      ],
      cta: "Start with Core", ctaLink: "/register",
    },
    color: "#0EA5E9", icon: Activity,
  },
  pro: {
    bg: {
      title: "Pro план",
      price: "59",
      tagline: "Задълбочен анализ и пълни доклади",
      forWhom: "За хора, които искат максимална яснота за здравето си — пълни генетични анализи, клинични доклади и повече AI анализи за месец.",
      description: "Pro планът добавя 3× повече AI анализи (120/мес), разширена нутригеномика с пълни генетични данни, мулти-агентен AI анализ и PDF доклади, които можете да споделите с лекаря си.",
      sections: [
        {
          title: "Всичко от Core плюс",
          icon: Check,
          items: [
            { icon: Brain, text: "120 AI анализа/месец (3× повече от Core) — достатъчно за чести анализи и консултации." },
            { icon: Zap, text: "AI анализ от екип специалисти — четирите AI помощника работят заедно и валидират отговорите си." },
            { icon: Dna, text: "Разширена нутригеномика — пълни генетични данни с научни източници и механизми." },
            { icon: FlaskConical, text: "Пълен клиничен анализ — подробни метаболитни модели и ендокринни оси." },
            { icon: FileText, text: "PDF здравен доклад — изтеглете пълен доклад с всички калкулатори и анализи." },
            { icon: FileText, text: "PDF генетичен доклад — вашият нутригеномичен профил във формат за споделяне с лекар." },
            { icon: Heart, text: "Триизмерен здравен аватар — визуализация на здравния ви статус по зони на тялото." },
            { icon: Zap, text: "Приоритетна обработка на заявките." },
          ]
        },
        {
          title: "За кого е подходящ",
          icon: Users,
          items: [
            { text: "Хора с хронични заболявания (диабет тип 2, автоимунни състояния), които искат задълбочен анализ." },
            { text: "Потребители, които вече ползват Core и искат повече от платформата." },
            { text: "Всеки, който иска да споделя пълни доклади с личния си лекар." },
          ]
        },
        {
          title: "Какво може да постигнете",
          icon: Target,
          items: [
            { text: "Пълно разбиране на генетичните си рискове и как да ги управлявате чрез хранене." },
            { text: "Документирани доклади за споделяне с лекар или диетолог." },
            { text: "По-бързи и по-точни отговори от AI благодарение на мулти-агентния анализ." },
          ]
        }
      ],
      cta: "Изберете Pro",
      ctaLink: "/register",
    },
    en: {
      title: "Pro Plan", price: "59",
      tagline: "In-depth analysis and full reports",
      forWhom: "For people who want maximum clarity about their health — full genetic analyses, clinical reports, and a generous monthly AI allotment.",
      description: "Pro adds 3× more AI analyses (120/mo), extended nutrigenomics, multi-agent analysis, and PDF reports you can share with your doctor.",
      sections: [
        { title: "Everything in Core plus", icon: Check, items: [
          { icon: Brain, text: "120 AI analyses/month (3× more than Core) — plenty for frequent analyses and consultations." },
          { icon: Zap, text: "Multi-agent AI analysis — four AI specialists work together." },
          { icon: Dna, text: "Extended nutrigenomics — full genetic data with scientific sources." },
          { icon: FlaskConical, text: "Full clinical analysis — detailed metabolic patterns and endocrine axes." },
          { icon: FileText, text: "PDF health report — download a full report with all analyses." },
          { icon: FileText, text: "PDF genetic report — nutrigenomic profile for sharing with your doctor." },
          { icon: Zap, text: "Priority processing." },
        ]},
        { title: "Who is it for", icon: Users, items: [
          { text: "People with chronic conditions wanting in-depth analysis." },
          { text: "Core users who want more from the platform." },
          { text: "Anyone wanting documented reports for their doctor." },
        ]},
        { title: "What you can achieve", icon: Target, items: [
          { text: "Full understanding of genetic risks and how to manage them through nutrition." },
          { text: "Documented reports for sharing with healthcare providers." },
          { text: "Faster, more accurate AI responses through multi-agent analysis." },
        ]}
      ],
      cta: "Choose Pro", ctaLink: "/register",
    },
    color: "#8B5CF6", icon: Star,
  },
  clinician: {
    bg: {
      title: "Лекарски план",
      price: "99",
      tagline: "Професионални инструменти за лекари и клиники",
      forWhom: "За лекари, диетолози и здравни специалисти, които искат да предложат на пациентите си персонализирано метаболитно проследяване с AI.",
      description: "Лекарският план дава достъп до пълния клиничен изглед на всички модули, управление на пациенти, и възможност за генериране на професионални доклади. Идеален за ендокринолози, диетолози и лекари, работещи с метаболитни и хранителни проблеми.",
      sections: [
        {
          title: "Какво е включено",
          icon: Check,
          items: [
            { icon: Users, text: "Управление на до 50 пациента — преглеждайте профили, анализи и прогрес." },
            { icon: FlaskConical, text: "Клиничен изглед навсякъде — пълни метаболитни модели, ендокринни оси, биологични области с научни механизми." },
            { icon: Dna, text: "Пълна нутригеномика — SNP генотипове, ClinVar данни, биологични пътища, хранителни добавки от научни бази." },
            { icon: Brain, text: "AI лабораторен анализ — качете лабораторни резултати и получете AI интерпретация." },
            { icon: FileText, text: "PDF доклади за пациенти — здравни и генетични доклади с вашия бранд." },
            { icon: Zap, text: "300 AI анализа/месец за клинични анализи и доклади." },
          ]
        },
        {
          title: "За кого е подходящ",
          icon: Users,
          items: [
            { text: "Ендокринолози, работещи с метаболитен синдром и инсулинова резистентност." },
            { text: "Диетолози, които искат персонализирани хранителни планове за пациентите си." },
            { text: "Клиники, търсещи цифрови инструменти за метаболитно проследяване." },
            { text: "Лекари, които искат да интегрират нутригеномика в практиката си." },
          ]
        },
        {
          title: "Какво може да постигнете",
          icon: Target,
          items: [
            { text: "По-ефективни консултации с готови AI анализи и доклади." },
            { text: "Проследяване на пациентския прогрес между визитите." },
            { text: "Научно обосновани хранителни препоръки на базата на генетичен профил." },
          ]
        }
      ],
      cta: "Регистрация като лекар",
      ctaLink: "/register",
    },
    en: {
      title: "Clinician Plan", price: "99 – 149",
      tagline: "Professional tools for doctors and clinics",
      forWhom: "For doctors, dietitians, and health professionals who want to offer personalized metabolic tracking with AI.",
      description: "The Clinician plan provides full clinical view across all modules, patient management, and professional report generation.",
      sections: [
        { title: "What's included", icon: Check, items: [
          { icon: Users, text: "Manage up to 50 patients — view profiles, analyses, and progress." },
          { icon: FlaskConical, text: "Clinical view everywhere — full metabolic patterns, endocrine axes, biological areas." },
          { icon: Dna, text: "Full nutrigenomics — SNP genotypes, ClinVar data, biological pathways." },
          { icon: Brain, text: "AI lab analysis — upload lab results and get AI interpretation." },
          { icon: FileText, text: "PDF reports for patients — health and genetic reports." },
          { icon: Zap, text: "300 AI analyses/month." },
        ]},
        { title: "Who is it for", icon: Users, items: [
          { text: "Endocrinologists working with metabolic syndrome." },
          { text: "Dietitians wanting personalized meal plans for patients." },
          { text: "Clinics seeking digital metabolic tracking tools." },
        ]},
        { title: "What you can achieve", icon: Target, items: [
          { text: "More effective consultations with ready AI analyses." },
          { text: "Track patient progress between visits." },
          { text: "Evidence-based nutrition recommendations based on genetic profile." },
        ]}
      ],
      cta: "Register as clinician", ctaLink: "/register",
    },
    color: "#10B981", icon: Stethoscope,
  },
};

export default function PlanDetailPage() {
  const { planId } = useParams();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const plan = PLANS[planId];
  if (!plan) return <Navigate to="/pricing" replace />;

  const t = plan[language] || plan.en;
  const PlanIcon = plan.icon;

  return (
    <div className={`min-h-screen ${isDark ? "bg-background text-foreground" : "bg-[#F9F9F7]"}`} data-testid={`plan-detail-${planId}`}>
      <SEO title={t.title} description={t.tagline} path={`/plan/${planId}`} />
      <SubpageNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <SubpageBackLink label={bg ? "Планове" : "Plans"} to="/pricing" />

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ backgroundColor: plan.color + "15" }}>
            <PlanIcon className="w-8 h-8" style={{ color: plan.color }} />
          </div>
          <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.title}
          </h1>
          <div className="flex items-baseline justify-center gap-1 mb-3">
            <span className="text-4xl font-bold" style={{ color: plan.color }}>{t.price === "0" ? "" : "\u20AC"}{t.price}</span>
            {t.price !== "0" && <span className={`text-sm ${isDark ? "text-slate-500" : "text-slate-400"}`}>/{bg ? "мес" : "mo"}</span>}
            {t.price === "0" && <span className={`text-sm font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>EUR</span>}
          </div>
          <p className={`text-base font-medium mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.tagline}</p>
          <p className={`text-sm max-w-lg mx-auto ${isDark ? "text-slate-400" : "text-[#4A4A68]"}`}>{t.forWhom}</p>
        </div>

        {/* Description */}
        <Card className={`mb-6 ${isDark ? "bg-card border-border" : "border-slate-100"}`}>
          <CardContent className="p-5">
            <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-[#4A4A68]"}`}>{t.description}</p>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="space-y-6 mb-8">
          {t.sections.map((section, si) => {
            const SIcon = section.icon;
            return (
              <div key={`section-${si}`} data-testid={`plan-section-${si}`}>
                <div className="flex items-center gap-2 mb-3">
                  <SIcon className="w-5 h-5" style={{ color: section.excluded ? "#EF4444" : plan.color }} />
                  <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{section.title}</h2>
                </div>
                <div className="space-y-2.5">
                  {section.items.map((item, ii) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={`item-${si}-${ii}`} className={`flex items-start gap-3 p-3 rounded-xl ${isDark ? "bg-card" : "bg-white"} border ${isDark ? "border-border" : "border-slate-100"}`}>
                        {section.excluded ? (
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-red-500 text-xs font-bold">-</span>
                          </div>
                        ) : ItemIcon ? (
                          <ItemIcon className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                        ) : (
                          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                        )}
                        <span className={`text-sm leading-relaxed ${section.excluded ? (isDark ? "text-slate-600 line-through" : "text-slate-400 line-through") : (isDark ? "text-slate-300" : "text-[#4A4A68]")}`}>
                          {item.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to={t.ctaLink}>
            <Button className="h-12 px-8 rounded-full text-sm font-semibold text-white shadow-lg" style={{ backgroundColor: plan.color }}
              data-testid={`plan-cta-${planId}`}>
              {t.cta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className={`text-xs mt-3 ${isDark ? "text-slate-600" : "text-slate-400"}`}>
            {bg ? "30-дневен безплатен пробен период. Без автоматично подновяване." : "30-day free trial. No auto-renewal."}
          </p>
        </div>

        {/* Back to pricing */}
        <div className="text-center mt-8">
          <Link to="/pricing" className={`text-sm font-medium ${isDark ? "text-slate-500 hover:text-slate-400" : "text-slate-400 hover:text-slate-500"}`}>
            {bg ? "Сравнете всички планове" : "Compare all plans"} &rarr;
          </Link>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}
